import React, { useEffect } from "react";
import { ShowToast } from "./toast";
import { getUser, getTenantId, setRefreshToken, setToken } from "./utils";
import { api } from "./api";
import { useApp } from "./app-context";
import { LottieLoading } from "../components/Loading/LottieLoading";
import AWS from "aws-sdk";
import { REGION } from "../env";
import { messaging } from "./firebase";
import { requestNotificationPermission } from "./request";
import { onMessage } from "firebase/messaging";
import { getPushNotificationContent, showForegroundPushNotification } from "./push-notification";
import { NotificationView } from "../components/NotificationView";
import posthog from "posthog-js";
import { getLoggerRef } from "../integrations/logger/logger-bridge";
import { identifyDatadogUser, getDatadogIdentityEpoch } from "./datadog";

type UserInfo = {
  id?: string;
  [key: string]: any;
};


export const UserContext = React.createContext({});

export function UserProvider({ children }) {
  const { userSession, userSessionLoading, setProfile, tenantError } = useApp();
  const [userInfo, setUserInfo] = React.useState<UserInfo>({});
  // Start loading when a token is already in storage so SSO (no Cognito session)
  // does not render the app tree with empty userInfo before fetchUser runs.
  const [userInfoLoading, setUserInfoLoading] = React.useState(
    () => !!(localStorage.getItem("token") || localStorage.getItem("id_token"))
  );
  const [roadMap, setRoadMap] = React.useState<any>(null);
  const [hasPendingNotifications, setHasPendingNotifications] = React.useState(false);
  const [credentials, setCredentials] = React.useState<any>(null);
  const [notificationData, setNotificationData] = React.useState<any>();
  const user = getUser();
  // Hold the shell while getSession is still running so we do not render
  // logged-in with empty userInfo, then flip to a spinner when getMe starts.
  const hasStoredAuth =
    !!localStorage.getItem("token") || !!localStorage.getItem("id_token");
  const isWaitingForSession =
    !userInfo?.id && userSessionLoading && hasStoredAuth && !tenantError;
  const isInitialUserLoading = !userInfo?.id && (userInfoLoading || isWaitingForSession);

  const fetchRoadMap = async () => {
    const roadMapResponse = await api.getRoadMap();
    setRoadMap(roadMapResponse.data);
  }

  const fetchCredentials = async () => {
    const credentialsResponse = await api.getCredentials();
    const credential = credentialsResponse.data;
    setCredentials(credential);
    if (credential?.AccessKeyId && credential?.SecretAccessKey) {
      // Initialize S3 with your credentials
      AWS.config.update({
        region: REGION, // Replace with your S3 bucket's region
        signatureVersion: "v4",
        credentials: {
          accessKeyId: credential.AccessKeyId,
          secretAccessKey: credential.SecretAccessKey,
          sessionToken: credential.SessionToken,
        }
      });
    }
  }


  const fetchProfile = async () => {
    const [profileResponse, dashboardInsightsResponse] = await Promise.all([
      api.getProfile(),
      api.getDashboardInsights().catch(() => null),
    ]);
    if (profileResponse.data?.profile) {
      setProfile({
        ...profileResponse.data.profile,
        dashboard_insights: dashboardInsightsResponse?.data ?? null,
      });
    }
  }

  const fetchUser = async (userId: string) => {
    try {
      setUserInfoLoading(true);
      // TODO: get user attributes from api
      // const userAttributes = await api.getUserAttributes(userId);
      const userProfile = await api.getMe();
      const user = userProfile?.data;
      await fetchRoadMap();
      await fetchCredentials();
      await fetchProfile();
      return user;
    } catch {
      ShowToast({
        message: "Something went wrong while fetching user info",
        type: "error",
      });
      return undefined;
    } finally {
      setUserInfoLoading(false);
    }
  };

  const fetchPendingNotifications = async () => {
    try {
      const pendingNotifications = await api.getPendingNotifications();
      setHasPendingNotifications(pendingNotifications.data);
    } catch (error) {
      posthog.captureException(error, {
        properties: {
          name: "fetch_pending_notifications_error",
          userId: user?.id,
        },
      });
    }
  }


  // Poll for pending notifications so the badge stays in sync without a page refresh
  const PENDING_NOTIFICATIONS_POLL_INTERVAL_MS = 10 * 1000; // 10 seconds
  useEffect(() => {
    if (!userInfo?.id) return;
    fetchPendingNotifications();
    const interval = setInterval(fetchPendingNotifications, PENDING_NOTIFICATIONS_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [userInfo?.id]);

  // Refresh S3 credentials before STS token expires (e.g. 1h) so signed URLs stay valid
  const CREDENTIAL_REFRESH_INTERVAL_MS = 50 * 60 * 1000; // 50 minutes
  useEffect(() => {
    if (!userInfo?.id) return;
    const interval = setInterval(fetchCredentials, CREDENTIAL_REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [userInfo?.id]);

  // Single source of truth for questionnaire completion (used by onboarding modal and tooltip tutorial).
  const questionnaireCompleted =
    userInfo?.wentTroughQuestionnaire === true || roadMap?.wentTroughQuestionnaire === true;

  const values = {
    userInfo,
    setUserInfo,
    fetchUser,
    loading: isInitialUserLoading,
    roadMap,
    fetchRoadMap,
    questionnaireCompleted,
    hasPendingNotifications,
    fetchPendingNotifications,
    credentials,
    fetchCredentials,
  };
  // A failed fetch returns no user; keeping whatever we already have beats
  // blanking userInfo out.
  const applyFetchedUser = (user: any, identityEpoch: number) => {
    if (!user) return;
    setUserInfo({
      ...user,
      //TODO: dummy role
      // role: userRoles.Manager,
    });
    identifyDatadogUser(user?.id, identityEpoch);
    if (user?.email) {
      posthog.identify(user.email, {
        email: user.email,
        tenant_id: getTenantId(),
        ...(user?.group ? { group: user.group } : {}),
      });
    }
  };

  // Cognito succeeded — the only trigger for the normal login path. Deliberately
  // not keyed on userSessionLoading: app-context publishes the session ~100ms
  // before it clears that flag, so depending on both would fetch twice per login.
  useEffect(() => {
    if (!userSession?.idToken) return;
    setToken(userSession.idToken.jwtToken);
    setRefreshToken(userSession.refreshToken?.token);
    const userId = userSession.idToken.payload["sub"];
    if (userId) {
      const identityEpoch = getDatadogIdentityEpoch();
      fetchUser(userId).then((user) => applyFetchedUser(user, identityEpoch));
    }
  }, [userSession]);

  // SSO never creates a Cognito userSession — Main admits those users via
  // id_token, and AuthCallback only writes localStorage. Only fall back to the
  // stored token and cached user.sub once getSession has genuinely finished
  // without a session, which makes this a no-op for every Cognito login.
  useEffect(() => {
    if (userSession?.idToken) return;
    if (userSessionLoading) return;
    const hasToken = localStorage.getItem("token") || localStorage.getItem("id_token");
    const userId = getUser()?.sub;
    if (hasToken && userId) {
      const identityEpoch = getDatadogIdentityEpoch();
      fetchUser(userId).then((user) => applyFetchedUser(user, identityEpoch));
    } else {
      setUserInfoLoading(false);
    }
  }, [userSession, userSessionLoading]);

  // Foreground FCM messages do not auto-display as native pushes — show them
  // via the service worker so they appear in Chrome's Notification Center.
  useEffect(() => {
    const userId = userSession?.idToken?.payload?.["sub"] ?? getUser()?.sub;
    if (!userId) return undefined;
    requestNotificationPermission(userId);
    const unsubscribe = onMessage(messaging, (payload) => {
      const content = getPushNotificationContent(payload);
      setNotificationData(content);
      void showForegroundPushNotification(payload);
      fetchPendingNotifications();
    });
    return unsubscribe;
  }, [userSession?.idToken?.jwtToken]);

  // Only block on the very first load. A background token refresh publishes a new
  // session, which re-runs fetchUser; gating on that too would drop the whole app
  // tree — including an in-progress call — behind a spinner once an hour.
  if (isInitialUserLoading) {
    return (
      <LottieLoading />
    )
  }
  return (<UserContext.Provider value={values}>
    {children}
    {notificationData && (
      <NotificationView
        title={notificationData.title}
        body={notificationData.body}
        onClose={() => setNotificationData(null)}
      />
    )}
  </UserContext.Provider>);
}

export function useUser() {
  const context = React.useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
}
